import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { getTrendingTagLinks } from '@videoblocks/shared-components';
import { ProgressiveImage } from '@videoblocks/storywind';

import { Button } from '../../../../builder-io/storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import { SearchFilterContentTypes } from '../../../Shared/enums';
import { SearchOption } from '../../../Shared/types';
import getAssetSource from '../../utils/getAssetSource';
import ProminentSearchContainer from './ProminentSearchContainer';

const imagesUrl = process.env.NEXT_PUBLIC_ASSET_PATH ?? '/images';

const eventTrackingForProminentSearch = () => {
  Telemetry.increment('homepagesearch.trending_keyword_click');
};

export const defaultContentTypes: SearchOption[] = [
  {
    id: SearchFilterContentTypes.All_videos_content_type,
    label: 'Video',
    placeholderText: 'Search video library...',
  },
  {
    id: SearchFilterContentTypes.Templates,
    label: 'Video Templates',
    placeholderText: 'Search templates library...',
  },
  {
    id: SearchFilterContentTypes.All_audio_content_type,
    label: 'Audio',
    placeholderText: 'Search audio library...',
  },
  {
    id: SearchFilterContentTypes.All_images_content_type,
    label: 'Images',
    placeholderText: 'Search image library...',
  },
];

export interface Props {
  headingFirstLine?: string;
  headingSecondLine?: string;
  subheading?: string;
  videoSrc: string;
  posterSrc: string;
  trendingTags?: string;
  contentTypes?: SearchOption[];
  showCallToAction: boolean;
  callToAction?: any;
  showSecondaryCta: boolean;
  secondaryCta?: any;
  posterAlt?: string;
  builderState: Record<string, any>;
  socialProof?: {
    logoAlt: string;
    logoImage: string;
  }[];
}

function ProminentSearch({
  headingFirstLine = 'Get Unlimited',
  headingSecondLine = 'Stock Media',
  subheading,
  trendingTags = 'Documentary,Love,Sport',
  videoSrc = '',
  posterSrc = '/home/video-homepage-hero-background.jpg',
  contentTypes = defaultContentTypes,
  showCallToAction,
  callToAction,
  showSecondaryCta,
  secondaryCta,
  posterAlt = '',
  builderState = {},
  socialProof = [],
}: Props) {
  const [contentType, setContentType] = useState(contentTypes[0]?.id);
  const hasAnyCtaButton =
    (showCallToAction && callToAction?.href) ||
    (showSecondaryCta && secondaryCta?.href);

  const isMobile = builderState?.state?.device === 'mobile';
  const numberOfLogos = socialProof.length;

  return (
    <div className="relative min-h-15rem">
      {/*Background*/}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden bg-gray-900">
        <div className="overflow-hidden w-full h-full">
          <div className="transition-all h-full whitespace-nowrap">
            {!isMobile && (
              <video
                className="hero-video w-full h-full object-cover"
                poster={getAssetSource(posterSrc)}
                loop
                autoPlay
                muted
              >
                <source src={getAssetSource(videoSrc)} type="video/mp4" />
              </video>
            )}
            {isMobile && (
              <ProgressiveImage
                className="absolute inset-0 object-cover w-full h-full"
                src={getAssetSource(posterSrc)}
                alt={posterAlt}
                shouldLazyLoad={true}
              />
            )}
          </div>
        </div>
      </div>
      {/*Header Container*/}
      <div className="flex w-full relative z-10 m-auto justify-center">
        <div
          className={twMerge(
            'gap-y-16 max-w-screen-2xl grid grid-cols-12 px-4 py-8 md:px-12 lg:px-24 pt-8 md:pt-20 lg:pt-20',
            hasAnyCtaButton && 'md:pt-12 md:pb-8'
          )}
        >
          {/*Header*/}
          <div className="justify-center flex flex-col col-span-full px-2 md:px-4 lg:px-4">
            {/*Header Text*/}
            <div className="justify-center flex flex-col pb-8">
              <h1 className="font-polysans font-bold text-white mb-0 text-center text-3xl lg:text-5xl mt-0">
                {headingFirstLine}
                <br />
                {headingSecondLine}
              </h1>
              {subheading && (
                <span className="font-bold text-white font-polysans text-center mt-2">
                  {subheading}
                </span>
              )}
            </div>
            {!isMobile && (
              <>
                <div
                  id="unifiedNav-search-app"
                  className="prominent-search-bar hidden lg:block"
                >
                  <ProminentSearchContainer
                    imagesUrl={imagesUrl}
                    contentTypes={contentTypes}
                    contentType={contentType}
                    setContentType={setContentType}
                    showDropdown={false}
                    showContentToggles
                  />
                </div>
                {trendingTags && (
                  <div
                    className={twMerge(
                      'flex justify-center',
                      hasAnyCtaButton && 'md:pb-6 lg:pb-6'
                    )}
                  >
                    <Text fontWeight="regular" className="text-white">
                      <span>Trending:&nbsp;</span>
                    </Text>
                    {trendingTags.split(',').map((tag, idx) => (
                      <a
                        className="homepage-trending text-white text-sm mobile-md:text-base ppro-sm:text-base md:text-base lg-text-base font-bold underline pr-1 underline-offset-5"
                        href={getTrendingTagLinks(
                          tag.toLowerCase().replace(/\s/g, '-'),
                          contentType
                        )}
                        onClick={eventTrackingForProminentSearch}
                        key={tag}
                      >
                        {tag}
                        {idx === trendingTags.split(',').length - 1 ? '' : ','}
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
            {hasAnyCtaButton && (
              <div className="flex items-center flex-col md:flex-row justify-center gap-4 md:gap-8 lg:gap-8">
                {showCallToAction &&
                  (!callToAction?.mobileOnly || isMobile) && (
                    <Button
                      className="justify-center w-full md:w-auto"
                      {...callToAction}
                    >
                      {callToAction?.text}
                    </Button>
                  )}
                {showSecondaryCta && (
                  <Button
                    className="justify-center w-full md:w-auto"
                    {...secondaryCta}
                  >
                    {secondaryCta?.text}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {numberOfLogos > 0 && (
        <div className="w-full relative z-10 m-auto pb-16 md:pb-24">
          <Text
            fontFamily="polysans"
            fontWeight="regular"
            className="text-center text-white mb-5"
          >
            <span>
              Trusted by industry-leading compan
              {numberOfLogos > 1 ? 'ies' : 'y'}
            </span>
          </Text>
          <div
            className={`flex flex-wrap content-center max-w-316px md:max-w-672px lg:max-w-1248px m-auto items-center ${
              numberOfLogos <= 6 ? 'justify-center gap-8' : 'justify-between'
            }`}
          >
            {socialProof.map(({ logoAlt, logoImage }, index) => (
              <div className="flex-initial" key={`logo_${index}`}>
                <img
                  src={logoImage}
                  alt={logoAlt}
                  className=" w-auto h-auto max-h-32px max-w-64px lg:max-h-54px lg:max-w-108px"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProminentSearch;
